import { Component, inject, NgZone, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faMicrophone, faStop, faCircle } from '@fortawesome/free-solid-svg-icons'
import { SpeechRecognitionService, SpeechRecognitionProvider } from '../../shared/services/speech-recognition'

@Component({
    standalone: true,
    imports: [FormsModule, FaIconComponent],
    template: `
        <div class="modal-header">
            <h5 class="modal-title">Voice Dictation POC v2</h5>
            <button type="button" class="btn-close" (click)="activeModal.dismiss()"></button>
        </div>
        <div class="modal-body">
            <div class="mb-3">
                <label class="form-label fw-semibold" for="techSelect">Technology</label>
                <select
                    id="techSelect"
                    class="form-select"
                    [(ngModel)]="selectedProviderName"
                    (ngModelChange)="onProviderChange()"
                    [disabled]="isListening"
                >
                    @for (p of providers; track p.name) {
                        <option [value]="p.name">{{ p.name }}</option>
                    }
                </select>
            </div>

            @if (!selectedProvider?.isSupported) {
                <div class="alert alert-warning">
                    {{ selectedProvider?.name }} is not supported in this browser. Try Chrome or Edge.
                </div>
            }

            <div class="d-flex align-items-center gap-3 mb-3">
                @if (!isListening) {
                    <button
                        class="btn btn-success btn-lg px-4 py-3"
                        (click)="startRecording()"
                        [disabled]="!selectedProvider?.isSupported"
                    >
                        <fa-icon [icon]="micIcon" class="me-2"></fa-icon> Start Recording
                    </button>
                } @else {
                    <button class="btn btn-danger btn-lg px-4 py-3" (click)="stopRecording()">
                        <fa-icon [icon]="stopIcon" class="me-2"></fa-icon> Stop Recording
                    </button>
                }

                <span class="fw-semibold" [class.text-success]="isListening" [class.text-secondary]="!isListening">
                    @if (isListening) {
                        <fa-icon [icon]="circleIcon" class="recording-pulse me-1"></fa-icon> Listening...
                    } @else if (errorMessage) {
                        <span class="text-danger">{{ errorMessage }}</span>
                    } @else {
                        Ready
                    }
                </span>
            </div>

            <div class="mb-2">
                <label class="form-label fw-semibold">Transcription</label>
                <div class="form-control transcription-area" readonly>
                    @if (!finalText && !interimText) {
                        <span class="text-muted fst-italic">Transcription will appear here...</span>
                    } @else {
                        <span>{{ finalText }}</span>
                        @if (interimText) {
                            <span class="text-muted fst-italic">{{ interimText }}</span>
                        }
                    }
                </div>
            </div>
        </div>
        <div class="modal-footer d-flex justify-content-between">
            <button class="btn btn-outline-secondary" (click)="clearTranscription()" [disabled]="!finalText && !interimText">
                Clear
            </button>
            <button class="btn btn-secondary" (click)="activeModal.dismiss()">Close</button>
        </div>
    `,
    styles: `
        .transcription-area {
            min-height: 200px;
            max-height: 400px;
            overflow-y: auto;
            white-space: pre-wrap;
            word-break: break-word;
            background-color: var(--bs-body-bg);
        }
        .recording-pulse {
            animation: pulse 1s ease-in-out infinite;
            color: #dc3545;
        }
        @keyframes pulse {
            0%,
            100% {
                opacity: 1;
            }
            50% {
                opacity: 0.3;
            }
        }
    `,
})
export class VoiceDictationModalComponent implements OnDestroy {
    activeModal = inject(NgbActiveModal)
    private zone = inject(NgZone)
    private speechService = inject(SpeechRecognitionService)

    micIcon = faMicrophone
    stopIcon = faStop
    circleIcon = faCircle

    providers: SpeechRecognitionProvider[]
    selectedProviderName: string
    selectedProvider: SpeechRecognitionProvider | undefined

    isListening = false
    finalText = ''
    interimText = ''
    errorMessage = ''

    constructor() {
        this.providers = this.speechService.getProviders()
        this.selectedProviderName = this.providers[0]?.name ?? ''
        this.selectedProvider = this.providers[0]
    }

    onProviderChange(): void {
        this.selectedProvider = this.speechService.getProvider(this.selectedProviderName)
        this.errorMessage = ''
    }

    startRecording(): void {
        if (!this.selectedProvider) return

        this.errorMessage = ''
        this.isListening = true

        this.selectedProvider.start(
            'en-US',
            (finalText, interimText) => {
                this.zone.run(() => {
                    this.finalText = finalText
                    this.interimText = interimText
                })
            },
            error => {
                this.zone.run(() => {
                    this.errorMessage = error
                    this.isListening = false
                })
            },
        )
    }

    stopRecording(): void {
        this.selectedProvider?.stop()
        this.isListening = false
        this.interimText = ''
    }

    clearTranscription(): void {
        this.finalText = ''
        this.interimText = ''
    }

    ngOnDestroy(): void {
        if (this.isListening) {
            this.selectedProvider?.stop()
        }
    }
}
